import * as THREE from 'three'
import { Archetype, ARCHETYPES, Heightmap, VrstaTerena, generisiProceduralnu } from './world/heightmap'
import { generisiEvropu } from './world/evropa'
import { buildTerrain, TerrainResult } from './world/terrain'
import { buildWater, WaterResult } from './world/water'
import { buildSky } from './world/sky'
import { buildCities, CitiesResult, Grad, izaberiGradove } from './world/cities'
import { buildRoads, PuteviResult } from './world/putevi'
import { RtsControls } from './camera/RtsControls'
import { Mreza } from './igra/mreza'
import { BOJE_IGRACA, IMENA_IGRACA, Prikaz } from './igra/prikaz'
import { Alatke, buildAlatke } from './ui/alatke'
import { buildHud } from './ui/hud'
import { uSvet } from './world/evropa'
import { hashSeed, randomSeedWord } from './core/rng'

const VELICINA_PROC = 1400
const VELICINA_EVROPA = 1800
const NAJVECA_MAPA = Math.max(VELICINA_PROC, VELICINA_EVROPA)
const mobilni = matchMedia('(pointer: coarse)').matches || innerWidth < 820

const canvas = document.getElementById('viewport') as HTMLCanvasElement

const renderer = new THREE.WebGLRenderer({
  canvas,
  antialias: !mobilni,
  powerPreference: 'high-performance',
})
renderer.setPixelRatio(Math.min(devicePixelRatio, mobilni ? 1.5 : 2))
renderer.setSize(innerWidth, innerHeight)
renderer.toneMapping = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 0.85
renderer.shadowMap.enabled = true
renderer.shadowMap.type = THREE.PCFSoftShadowMap

const scene = new THREE.Scene()
const camera = new THREE.PerspectiveCamera(48, innerWidth / innerHeight, 1, NAJVECA_MAPA * 24)

// Nebo i senke se prave jednom, po najvećoj mapi — obe vrste terena ih dele.
const nebo = buildSky(NAJVECA_MAPA)
scene.add(nebo.sun, nebo.sun.target, nebo.hemi)
const magla = new THREE.Fog(nebo.horizon, VELICINA_PROC * 0.75, VELICINA_PROC * 3.4)
scene.fog = magla

let hm!: Heightmap
let teren: TerrainResult | null = null
let voda: WaterResult | null = null
let gradoviVid: CitiesResult | null = null
let putevi: PuteviResult | null = null
let kontrole!: RtsControls
let visinaSunca = 26
let poslednjeputanje: unknown[] = []

let mreza!: Mreza
let prikaz: Prikaz | null = null
let alatke: Alatke | null = null
let izabrana: number | null = null
let igracAktivan = 0
let nivoAktivan = 0

function ocisti(): void {
  teren?.mesh.removeFromParent()
  teren?.dispose()
  teren = null
  voda?.mesh.removeFromParent()
  voda?.dispose()
  voda = null
  gradoviVid?.group.removeFromParent()
  gradoviVid?.dispose()
  gradoviVid = null
  putevi?.group.removeFromParent()
  putevi?.dispose()
  putevi = null
  prikaz?.group.removeFromParent()
  prikaz?.dispose()
  prikaz = null
}

function generisi(vrsta: VrstaTerena, arhetip: Archetype, seedTekst: string): string {
  const konacniSeed = seedTekst || randomSeedWord()
  const seed = hashSeed(konacniSeed)
  ocisti()

  let gradovi: Grad[]
  let skalaGradova: number
  let visinaNatpisa: number

  if (vrsta === 'evropa') {
    const evropa = generisiEvropu({
      size: VELICINA_EVROPA,
      seed,
      res: mobilni ? 384 : 576,
    })
    hm = evropa.hm
    gradovi = evropa.gradovi
    // Evropa ima tri puta više gradova nego proceduralna mapa, pa su markeri sitniji.
    skalaGradova = hm.size / 2900
    visinaNatpisa = 0.018
    poslednjeputanje = evropa.putanje
    putevi = buildRoads(evropa.putanje, hm.size)
    scene.add(putevi.group)
  } else {
    hm = generisiProceduralnu({
      arhetip,
      seed,
      size: VELICINA_PROC,
      res: mobilni ? 320 : 512,
    })
    gradovi = izaberiGradove(hm)
    skalaGradova = hm.size / 1400
    visinaNatpisa = 0.024
  }

  magla.near = hm.size * 0.75
  magla.far = hm.size * 3.4

  teren = buildTerrain(hm)
  scene.add(teren.mesh)

  voda = buildWater(hm.size, teren.depthTexture, magla)
  scene.add(voda.mesh)

  gradoviVid = buildCities(gradovi, skalaGradova, visinaNatpisa)
  scene.add(gradoviVid.group)

  kontrole?.dispose()
  kontrole = new RtsControls(camera, canvas, hm)

  // Mreža kula se ne prenosi na novu mapu — visine su druge, pa i vidljivost.
  mreza = new Mreza(hm)
  prikaz = new Prikaz(hm, mreza)
  prikaz.postaviRezoluciju(innerWidth, innerHeight)
  scene.add(prikaz.group)
  izabrana = null
  osveziMrezu()

  return konacniSeed
}

/** Jedno mesto sa koga se osvežava sve što se od mreže vidi. */
function osveziMrezu(): void {
  if (!prikaz) return
  prikaz.osveziKule()
  prikaz.osveziVeze(izabrana)
  alatke?.setUkloniMoguce(izabrana !== null)
  alatke?.setTablu(
    BOJE_IGRACA.map((boja, i) => {
      const kula = mreza.kule.filter((k) => k.igrac === i).length
      const lanac = kula ? mreza.najduziLanac(i) : { kula: 0, duzina: 0 }
      return {
        ime: IMENA_IGRACA[i],
        boja,
        kula,
        veza: mreza.brojVeza(i),
        lanac: kula ? `${lanac.kula} · ${Math.round(lanac.duzina)}` : '—',
      }
    }),
  )
}

const pocetniArhetip: Archetype = ARCHETYPES[0]
const pocetniSeed = generisi('proceduralna', pocetniArhetip, '')

nebo.setSun(visinaSunca, 128)
nebo.refreshEnv(renderer, scene)

const hud = buildHud({
  vrsta: 'proceduralna',
  arhetip: pocetniArhetip,
  seed: pocetniSeed,
  visinaSunca,
  onGenerate(vrsta, arhetip, seed) {
    // Evropa se računa skoro sekundu; pustimo browser da prvo iscrta
    // zatamnjen panel, pa tek onda blokiramo nit. Namerno setTimeout, ne
    // requestAnimationFrame — u tabu u pozadini rAF stoji, pa se generisanje
    // nikad ne bi ni pokrenulo.
    hud.setBusy(true)
    setTimeout(() => {
      hud.setSeed(generisi(vrsta, arhetip, seed))
      hud.setBusy(false)
    }, 30)
  },
  onReset() {
    kontrole.reset()
  },
  onSunce(stepeni, konacno) {
    visinaSunca = stepeni
    nebo.setSun(visinaSunca, 128)
    if (konacno) nebo.refreshEnv(renderer, scene)
  },
})

// --- očitavanje pozicije pod kursorom ---
const tackaPodKursorom = new THREE.Vector3()
let poslednjiPokazivac: { clientX: number; clientY: number } | null = null
let imaOcitavanje = false

canvas.addEventListener('pointermove', (e) => {
  poslednjiPokazivac = { clientX: e.clientX, clientY: e.clientY }
})
canvas.addEventListener('pointerleave', () => {
  poslednjiPokazivac = null
})

addEventListener('resize', () => {
  camera.aspect = innerWidth / innerHeight
  camera.updateProjectionMatrix()
  renderer.setSize(innerWidth, innerHeight)
  prikaz?.postaviRezoluciju(innerWidth, innerHeight)
})

// --- postavljanje kula ---

alatke = buildAlatke({
  igrac: igracAktivan,
  nivo: nivoAktivan,
  onIgrac(i) {
    igracAktivan = i
  },
  onNivo(n) {
    nivoAktivan = n
  },
  onOcisti() {
    mreza.ocisti()
    izabrana = null
    osveziMrezu()
  },
  onUkloni() {
    if (izabrana === null) return
    mreza.ukloni(izabrana)
    izabrana = null
    osveziMrezu()
  },
})
osveziMrezu()

const raycaster = new THREE.Raycaster()
const tackaKlika = new THREE.Vector3()
let pritisak: { x: number; y: number } | null = null

function ndcOd(e: { clientX: number; clientY: number }): THREE.Vector2 {
  const r = canvas.getBoundingClientRect()
  return new THREE.Vector2(
    ((e.clientX - r.left) / r.width) * 2 - 1,
    -((e.clientY - r.top) / r.height) * 2 + 1,
  )
}

function kulaPod(e: { clientX: number; clientY: number }): number | null {
  if (!prikaz) return null
  raycaster.setFromCamera(ndcOd(e), camera)
  return prikaz.pogodi(raycaster)
}

canvas.addEventListener('pointerdown', (e) => {
  if (e.button === 0) pritisak = { x: e.clientX, y: e.clientY }
})

canvas.addEventListener('pointerup', (e) => {
  const p = pritisak
  pritisak = null
  if (!p || e.button !== 0) return
  // Prevlačenje pomera mapu, klik u mestu postavlja kulu. Namerno bez
  // vremenskog ograničenja — spor, pažljiv klik je i dalje klik.
  if (Math.hypot(e.clientX - p.x, e.clientY - p.y) > 5) return

  const pogodjena = kulaPod(e)
  if (pogodjena !== null) {
    izabrana = izabrana === pogodjena ? null : pogodjena
    osveziMrezu()
    return
  }
  if (!kontrole.tackaPodMisem(e, tackaKlika)) return
  if (mreza.dodaj(tackaKlika.x, tackaKlika.z, igracAktivan, nivoAktivan)) {
    izabrana = null
    osveziMrezu()
  }
})

const sat = new THREE.Clock()
let akumulatorFps = 0
let brojFrejmova = 0
let fps = 0
let brojac = 0

function petlja(): void {
  const dt = Math.min(sat.getDelta(), 0.05)
  const t = sat.getElapsedTime()

  kontrole.update(dt)
  voda?.update(t, camera, nebo.sunDir)
  prikaz?.update(dt, kontrole.udaljenost)

  // Očitavanje je dovoljno da se osvežava nekoliko puta u sekundi.
  if (++brojac % 5 === 0) {
    imaOcitavanje = poslednjiPokazivac
      ? kontrole.tackaPodMisem(poslednjiPokazivac, tackaPodKursorom)
      : false
    osveziPregled()
    const smer = ((-kontrole.azimut * 180) / Math.PI + 360) % 360
    hud.setReadout(
      imaOcitavanje
        ? `<b>${Math.round(tackaPodKursorom.x)}</b> , <b>${Math.round(tackaPodKursorom.z)}</b>` +
            ` &nbsp; visina <b>${Math.max(0, Math.round(tackaPodKursorom.y))} m</b>` +
            ` &nbsp;·&nbsp; smer <b>${Math.round(smer)}°</b> &nbsp;·&nbsp; ${fps} fps`
        : `smer <b>${Math.round(smer)}°</b> &nbsp;·&nbsp; visina kamere` +
            ` <b>${Math.round(kontrole.udaljenost)}</b> &nbsp;·&nbsp; ${fps} fps`,
    )
  }

  akumulatorFps += dt
  brojFrejmova++
  if (akumulatorFps >= 0.5) {
    fps = Math.round(brojFrejmova / akumulatorFps)
    akumulatorFps = 0
    brojFrejmova = 0
  }

  renderer.render(scene, camera)
  requestAnimationFrame(petlja)
}

/**
 * Duh kule pod kursorom. Ovo je ono zbog čega igračka postoji: pre nego što
 * spustiš kulu vidiš šta odatle vidiš, i šta ti brdo pojede.
 */
function osveziPregled(): void {
  if (!prikaz || !alatke) return

  if (!poslednjiPokazivac || !imaOcitavanje || kulaPod(poslednjiPokazivac) !== null) {
    prikaz.sakrijPregled()
    if (izabrana === null) prikaz.osveziVeze(null)
    alatke.setNagovestaj('')
    return
  }

  const p = prikaz.prikaziPregled(
    tackaPodKursorom.x,
    tackaPodKursorom.z,
    nivoAktivan,
    igracAktivan,
  )

  if (!p.moze) {
    alatke.setNagovestaj(`<s>ne može — ${p.razlog}</s>`)
    return
  }
  const delovi: string[] = []
  delovi.push(p.veza ? `<b>${p.veza} ${rec(p.veza, 'veza', 'veze', 'veza')}</b>` : '<em>nema veza</em>')
  if (p.prekid) delovi.push(`<s>${p.prekid} iza brda</s>`)
  delovi.push('<em>klik da postaviš</em>')
  alatke.setNagovestaj(delovi.join(' &nbsp;·&nbsp; '))
}

function rec(n: number, jedan: string, dva: string, mnogo: string): string {
  const d = n % 10
  const dd = n % 100
  if (d === 1 && dd !== 11) return jedan
  if (d >= 2 && d <= 4 && (dd < 12 || dd > 14)) return dva
  return mnogo
}

requestAnimationFrame(petlja)

// Kuka za pregled scene iz konzole tokom razvoja.
if (import.meta.env.DEV) {
  Object.assign(window as unknown as Record<string, unknown>, {
    __telegraf: {
      scene, camera, renderer, nebo,
      // Teren i kamera se prave iznova pri svakoj generaciji, pa moraju geterima.
      get hm() { return hm },
      get kontrole() { return kontrole },
      get putanje() { return poslednjeputanje },
      get mreza() { return mreza },
      get prikaz() { return prikaz },
      osveziMrezu,
      /** Brzo pozicioniranje na geografske koordinate, za proveru mape. */
      letiNa(lon: number, lat: number, distanca = 260) {
        const { x, z } = uSvet(lon, lat, hm.size)
        kontrole.postavi({ x, z, distanca, pitch: 0.45 })
      },
    },
  })
}

// Uvodni ekran sklanjamo po prvom kadru, ali i po isteku vremena —
// u pozadinskom tabu rAF stoji, pa bi inače ostao zauvek.
let bootSklonjen = false
function skloniBoot(): void {
  if (bootSklonjen) return
  bootSklonjen = true
  const boot = document.getElementById('boot')
  boot?.classList.add('gone')
  setTimeout(() => boot?.remove(), 600)
}
requestAnimationFrame(() => requestAnimationFrame(skloniBoot))
setTimeout(skloniBoot, 1500)
